import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    this.#showInitialSlide();
  }

  /**
   * RECLAIM FIX: open the gallery on the selected variant's photo.
   *
   * The slideshow marks the initial slide as "current" but never scrolls to it (its select() call sees the slide is
   * already current and returns early). So after a variant change the thumbnail highlights the right photo while the
   * big image stays on photo 1. Here we reset the slideshow's current slide to 0 and select the real one so it scrolls.
   */
  #showInitialSlide() {
    const slideshow = this.querySelector('slideshow-component');
    const index = slideshow ? parseInt(slideshow.getAttribute('initial-slide') || '0', 10) : 0;

    if (!slideshow || !index) return;

    let frames = 0;

    const apply = () => {
      if (!this.isConnected || !slideshow.isConnected) return true;
      if (typeof slideshow.select !== 'function') return false;

      slideshow.current = 0;
      slideshow.select(index, undefined, { animate: false });

      return slideshow.current === index;
    };

    const attempt = () => {
      if (apply()) {
        // The slideshow can finish its own setup a moment later, so double check twice.
        [250, 600].forEach((delay) => {
          setTimeout(() => {
            if (this.isConnected && slideshow.current === 0) apply();
          }, delay);
        });
        return;
      }

      if (++frames < 40) {
        requestAnimationFrame(attempt);
      } else if (slideshow.isConnected) {
        slideshow.current = index;
      }
    };

    requestAnimationFrame(() => requestAnimationFrame(attempt));
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
